import { theme } from '@theme';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { usePortfolioBulkActionsContainer } from '../../../../../hooks/UsePortfolioBulkActionsContainer';
import { AppState } from '../../../../../store/AppState';
import { PortfolioState } from '../../../../../store/portfolio/PortfolioReducer';
import { BulkExportListResource } from '../../../../../types/PortfolioBulkActionResource';
import { PortfolioResource } from '../../../../../types/PortfolioResource';
import ButtonComponent from '../../../../shared/button/ButtonComponent';
import ConfirmationModal from '../../../../shared/modals/confirmation-modal/ConfirmationModal';
import { Typography } from '../../../../style';

type ExportListProps = {
  selectedRecords: Array<PortfolioResource>;
  selectedRecordsCount: number;
  totalRecordsCount: number;
  isAllRowsSelected: boolean;
  isClearSelectionLink: boolean;
  toggleSelectedRows: (value: boolean) => void;
};

const ExportList: FC<ExportListProps> = (props) => {
  const [isExportListModalOpen, setIsExportListModalOpen] = useState<boolean>(false);
  const [isExcessRowsSelected, setIsExcessRowsSelected] = useState<boolean>(false);
  const [isChecked, setChecked] = useState<boolean>(false);
  const [isCheckedTerms, setCheckedTerms] = useState<boolean>(false);
  const [isExportEnable, setExportEnable] = useState<boolean>(false);
  const { bulkExportList, createBulkExportPayload } = usePortfolioBulkActionsContainer();
  const { isPortfolioBulkExportInPorgress } = useSelector<AppState, PortfolioState>(
    (state) => state.portfolio,
  );

  const handleChange = () => {
    setChecked(!isChecked);
  };
  const handleChangeTerms = () => {
    setCheckedTerms(!isCheckedTerms);
  };

  const handleExportButton = () => {
    if (isChecked && isCheckedTerms) {
      setExportEnable(true);
    } else {
      setExportEnable(false);
    }
  };
  useEffect(() => {
    handleExportButton();
  }, [isChecked, isCheckedTerms]);

  useEffect(() => {
    setIsExcessRowsSelected(
      props.totalRecordsCount > 10000 && props.isAllRowsSelected && props.isClearSelectionLink,
    );
  }, [
    props.totalRecordsCount,
    props.selectedRecordsCount,
    props.isClearSelectionLink,
    props.isAllRowsSelected,
  ]);

  const openExportListModal = (): void => {
    setIsExportListModalOpen(true);
  };

  const handleCancel = () => {
    setIsExportListModalOpen(false);
    setChecked(false);
    setCheckedTerms(false);
  };

  const handleSubmit = (): void => {
    let payload: BulkExportListResource = createBulkExportPayload(
      props.isAllRowsSelected,
      props.isClearSelectionLink,
      props.selectedRecords,
    );
    const successCallback = () => {
      props.toggleSelectedRows(false);
    };
    setIsExportListModalOpen(false);
    setChecked(false);
    setCheckedTerms(false);
    bulkExportList(payload, successCallback);
  };

  const bodyComponent: JSX.Element = (
    <>
      <Typography size={theme.typography.h5} fontWeight={theme.typography.fontWeights.semibold}>
        {isExcessRowsSelected ? (
          <>
            There is a limit of 10,000 records that can be saved to a file.
            <Typography size={theme.typography.h5}>
              &nbsp;Your export will be limited to the first 10,000 records.
            </Typography>
          </>
        ) : (
          <>
            {props.isClearSelectionLink && props.isAllRowsSelected
              ? props.totalRecordsCount
              : props.selectedRecordsCount}
            &nbsp;records will be saved in a CSV file.
          </>
        )}
      </Typography>
      <Typography size={theme.typography.h5} paddingTop={15} display={'block'}>
        <label className='terms-conditions'>
          <input
            className='terms-checkbox'
            type='checkbox'
            checked={isChecked}
            onChange={() => handleChange()}
            data-testid='terms-checkbox'
          />
          <span>
            As an authorized AutomotiveMastermind subscriber, I agree to only use the exported data
            for direct marketing purposes. This data may not be published, uploaded, resold, or
            given to third parties without the express permission of AutomotiveMastermind.
          </span>
        </label>
      </Typography>
      <Typography size={theme.typography.h5} paddingTop={15} display={'block'}>
        <label className='terms-conditions'>
          <input
            className='terms-checkbox'
            type='checkbox'
            checked={isCheckedTerms}
            onChange={() => handleChangeTerms()}
            data-testid='term-checkbox'
          />
          <span>
            I agree to the current AutmotiveMastermind Terms of Service and Privacy Policy.
          </span>
        </label>
      </Typography>
    </>
  );

  return (
    <>
      <ButtonComponent
        color={theme.palette.mediumGray}
        wrapperBackgroundColor={theme.palette.blackOlive}
        text={isPortfolioBulkExportInPorgress ? 'Exporting...' : 'Export List...'}
        fontWeight={theme.typography.fontWeights.semibold}
        outerPadding={'0px'}
        innerPadding={'7px 12px'}
        borderRadius={4}
        onClick={openExportListModal}
        backgroundColor={
          isPortfolioBulkExportInPorgress ? theme.palette.disabledGrey : theme.palette.white
        }
        disabled={isPortfolioBulkExportInPorgress}
        textAlign='left'
      />
      <ConfirmationModal
        isOpen={isExportListModalOpen}
        handleSubmitClick={handleSubmit}
        handleCancelClick={handleCancel}
        cancelButtonText='Cancel'
        submitButtonText='Export'
        title='Export List'
        bodyComponent={bodyComponent}
        showCloseIcon={false}
        padding={'20px 20px 20px 20px'}
        minHeight={'225px'}
        width={'480px'}
        submitButtonBackgroundColor={
          !isExportEnable ? theme.palette.backgroundGray : theme.palette.text.purple
        }
        submitButtonTextColor={!isExportEnable ? theme.palette.lightGreyTwo : theme.palette.white}
        submitButtonDisabled={!isExportEnable}
        shouldCloseOnOverlayClick
      />
    </>
  );
};

export default ExportList;
